'use client';

import { PropsWithChildren } from 'react';

import classNames from 'classnames';
import { useTranslations } from 'next-intl';

import { AssetTradeConditions } from '@/api/trade-conditions/types';
import { RecapAsset } from '@/api/trading/types';
import { useRecap } from '@/api/trading/useRecap';
import { calcSpread } from '@/api/utils';
import { IconTrendDown } from '@/blocks/components/Icons/IconTrendDown';
import { IconTrendUp } from '@/blocks/components/Icons/IconTrendUp';
import { LinkSignup } from '@/blocks/components/PlatformLink';
import { WithClassName } from '@/blocks/components/types';
import { EventBlockNames } from '@/providers/analytics/types';
import button from '@/styles/button.module.scss';
import typography from '@/styles/typography.module.scss';

import { Card, cardStyles } from '@/app/[lang]/price-chart/[category]/[symbol]/components/Card';
import { Asset as LocalAsset } from '@/app/[lang]/price-chart/[category]/types';

import styles from './styles.module.scss';

function BuySellButton(props: { side: 'buy' | 'sell' }) {
  const tr = useTranslations('pages.priceCharts.asset.sections.sidebar.buySell');
  const text = props.side === 'buy' ? tr.rich('buy') : tr.rich('sell');
  const icon = props.side === 'buy' ? <IconTrendUp /> : <IconTrendDown />;
  return (
    <LinkSignup
      className={classNames(
        button.btn,
        button.btnM,
        button.btnIconLeft,
        props.side === 'buy' ? button.btnPrimary : button.btnPrimaryRed,
      )}
      data-event-label={text}
    >
      {icon}
      {text}
    </LinkSignup>
  );
}

// for buyers/sellers
function Progress(props: { value: number }) {
  return (
    <div className={styles.progress}>
      <span
        style={{
          width: props.value + '%',
          minWidth: props.value + '%',
          maxWidth: props.value + '%',
        }}
      ></span>
      <span></span>
    </div>
  );
}

export function BuySellButtons({ children, className }: PropsWithChildren & WithClassName) {
  return (
    <div
      className={classNames(styles.buttonsContainer, className)}
      data-event-block-name={EventBlockNames.buy_sell_widget}
    >
      <BuySellButton side={'buy'} />
      <span className={classNames(typography.bodyM, styles.spread)}>{children}</span>
      <BuySellButton side={'sell'} />
    </div>
  );
}

export function BuySellWidget({
  asset,
  initialData,
  tradeConditions,
}: {
  asset: LocalAsset;
  initialData: RecapAsset[];
  tradeConditions: Record<string, AssetTradeConditions> | null;
}) {
  const { markets } = useRecap(undefined, initialData);
  const tr = useTranslations('pages.priceCharts.asset.sections.sidebar.buySell.marketHours');

  if (!markets) {
    return null;
  }

  const assetData = markets.find((a) => a.name === asset?.name);

  if (!assetData) {
    return null;
  }

  const spread = calcSpread(assetData.ask, assetData.bid);

  const tradingHours =
    Array.from(
      new Set(Object.entries(tradeConditions || []).map(([_, conditions]) => conditions.tradingHours)),
    )[0] || null;

  return (
    <Card className={classNames(cardStyles.cardPaddingRegular)} innerClassName={styles.buySellContainer}>
      <BuySellButtons>{spread}</BuySellButtons>
      {tradingHours ? (
        <div className={styles.tradingHours}>
          <p className={classNames(typography.subheadingM, typography.semibold)}>{tr('title')}</p>
          <p className={typography.bodyM}>{tradingHours}</p>
        </div>
      ) : null}
      {/*<div className={styles.progressWithLabels}>*/}
      {/*  <div className={styles.progressLabels}>*/}
      {/*    <div>*/}
      {/*      Buyers <span>TODO%</span>*/}
      {/*    </div>*/}
      {/*    <div>*/}
      {/*      Sellers <span>TODO%</span>*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*  <Progress value={80} />*/}
      {/*</div>*/}
    </Card>
  );
}
